import { Injectable, signal, computed } from '@angular/core';
import { TimelineEvent, TimelineEventType } from '../../interfaces/dev-tool/timeline.interface';

// Re-export for backward compatibility
export { TimelineEvent, TimelineEventType };

@Injectable({ providedIn: 'root' })
export class TimelineTrackerService {
  private events = signal<TimelineEvent[]>([]);
  private maxEvents = 1000;
  private startTime = Date.now();
  
  getEvents = computed(() => this.events());
  
  constructor() {
    this.trackInitialLoad();
  }
  
  private trackInitialLoad() {
    this.addEvent({
      type: 'performance',
      title: 'Application Started',
      description: 'Dev tools initialized',
      status: 'success'
    });
  }
  
  addEvent(event: Omit<TimelineEvent, 'id' | 'timestamp'>) {
    const newEvent: TimelineEvent = {
      id: crypto.randomUUID(),
      timestamp: Date.now() - this.startTime,
      ...event
    };
    
    this.events.update(events => {
      const updated = [...events, newEvent];
      return updated.slice(-this.maxEvents);
    });
  }
  
  trackConsole(type: string, message: string, data?: any) {
    this.addEvent({
      type: 'console',
      title: `Console ${type}`,
      description: message,
      data,
      status: type === 'error' ? 'error' : type === 'warn' ? 'warning' : 'success'
    });
  }
  
  trackAPI(method: string, url: string, status: number, duration: number, data?: any) {
    this.addEvent({
      type: 'api',
      title: `${method} ${url}`,
      description: `Response: ${status}`,
      data,
      duration,
      status: status >= 200 && status < 300 ? 'success' : 'error'
    });
  }
  
  trackStateChange(componentName: string, property: string, oldValue: any, newValue: any) {
    this.addEvent({
      type: 'state-change',
      title: `State Changed: ${componentName}`,
      description: `${property}: ${JSON.stringify(oldValue)} → ${JSON.stringify(newValue)}`,
      data: { componentName, property, oldValue, newValue },
      status: 'success'
    });
  }
  
  trackComponent(action: 'mount' | 'unmount' | 'update', componentName: string) {
    this.addEvent({
      type: 'component',
      title: `Component ${action}`,
      description: componentName,
      status: 'success'
    });
  }
  
  trackUserAction(action: string, target: string, data?: any) {
    this.addEvent({
      type: 'user-action',
      title: `User ${action}`,
      description: target,
      data,
      status: 'success'
    });
  }
  
  trackNavigation(from: string, to: string) {
    this.addEvent({
      type: 'navigation',
      title: 'Navigation',
      description: `${from} → ${to}`,
      status: 'success'
    });
  }
  
  trackError(error: Error, context?: string) {
    this.addEvent({
      type: 'error',
      title: 'Error',
      description: error.message,
      data: { stack: error.stack, context },
      status: 'error'
    });
  }
  
  clear() {
    this.events.set([]);
    this.startTime = Date.now();
    this.trackInitialLoad();
  }
  
  exportTimeline(): string {
    return JSON.stringify(this.events(), null, 2);
  }
}